/**
 * Shared settlement status helpers.
 *
 * Semantics:
 * - settled / Paid: expense.isPaid === true (reclaim fully covered)
 * - partial: unpaid but reimbursement.amountPaid > 0
 * - pending: unpaid with no contributions (or no reimbursement needed still outstanding)
 * - none: personal / no-reimbursement expenses that are unpaid
 */

export function getAmountPaid(expense) {
  const fromReimbursement = parseFloat(expense?.reimbursement?.amountPaid);
  if (!Number.isNaN(fromReimbursement) && fromReimbursement > 0) {
    return fromReimbursement;
  }
  const payments = expense?.payments;
  if (Array.isArray(payments) && payments.length) {
    return payments.reduce((sum, payment) => {
      const amount = parseFloat(payment?.amount);
      return sum + (Number.isNaN(amount) ? 0 : amount);
    }, 0);
  }
  return 0;
}

export function getRemainingAmount(expense) {
  const remaining = parseFloat(expense?.reimbursement?.remainingAmount);
  if (!Number.isNaN(remaining)) {
    return remaining;
  }
  const owed = parseFloat(expense?.reimbursement?.reimbursementAmount);
  if (!Number.isNaN(owed)) {
    return Math.max(0, owed - getAmountPaid(expense));
  }
  return null;
}

/**
 * @returns {'settled'|'partial'|'pending'|'none'}
 */
export function getPaymentStatus(expense) {
  if (!expense) return 'none';
  if (expense.isPaid || expense.paid) return 'settled';

  const needsReimbursement =
    expense.reimbursement?.needsReimbursement === true ||
    (expense.splitMethod &&
      expense.splitMethod !== 'NO_REIMBURSEMENT' &&
      expense.splitMethod !== 'none');

  const amountPaid = getAmountPaid(expense);
  const remaining = getRemainingAmount(expense);
  if (needsReimbursement && amountPaid > 0) {
    // Contributions already cover reclaim even if isPaid flag lags
    if (remaining != null && remaining <= 0) {
      return 'settled';
    }
    return 'partial';
  }

  if (needsReimbursement) {
    return 'pending';
  }

  return 'none';
}

export function getPaymentStatusMeta(expense) {
  const status = getPaymentStatus(expense);
  const remaining = getRemainingAmount(expense);
  const amountPaid = getAmountPaid(expense);

  switch (status) {
    case 'settled':
      return {
        status,
        label: 'Paid',
        badgeBg: 'success',
        amountPaid,
        remainingAmount: 0,
      };
    case 'partial':
      return {
        status,
        label: 'Partial',
        badgeBg: 'info',
        amountPaid,
        remainingAmount: remaining,
      };
    case 'pending':
      return {
        status,
        label: 'Pending',
        badgeBg: 'warning',
        amountPaid: 0,
        remainingAmount: remaining,
      };
    default:
      return {
        status,
        label: 'Open',
        badgeBg: 'secondary',
        amountPaid: 0,
        remainingAmount: null,
      };
  }
}
